<template>
  <div
    class="w-full justify-center bg-dark-gray px-5 py-4 font-medium md:px-0 md:pt-10 lg:px-10 lg:py-6"
  >
    <section v-if="screenWidth < 730">
      <div class="flex items-start gap-5">
        <div class="-ml-2">
          <popper-badge
            v-if="!loading"
            :is-header-show="true"
            color-scheme="light"
          />
        </div>
        <div class="-ml-2">
          <h1
            v-if="!loading"
            class="broker-name mt-1 text-left text-base text-white"
            :class="{}"
          >
            {{ getName(broker) }}
          </h1>
          <div
            v-else
            class="loading-light h-8 w-72"
          />

          <div
            id="mc-wrap"
            class="mt-2"
          >
            <div
              v-if="!loading"
              class="flex flex-1 flex-wrap items-center gap-x-2"
            >
              <span
                v-for="identifier in broker?.identifiers"
                :key="identifier.value"
                class="whitespace-nowrap text-xs uppercase text-gray-over-dark"
              >
                {{ formatIsType(identifier.is_type) }} {{ identifier.value }}
              </span>
            </div>
            <div
              v-else
              class="flex flex-row items-center gap-x-2"
            >
              <div class="loading-light-2 h-4" />
              <div class="loading-light-2 h-4" />
            </div>
          </div>
        </div>
        <div
          v-if="assessmentResult"
          class="mr-3 flex h-10 select-none items-center pt-1.5"
        >
          <custom-badge
            v-if="!loading && assessmentResult == 'fail'"
            bg-color-class="bg-orange-200"
            color-class="text-orange-600"
            size="md"
            class="inline-flex self-start capitalize"
            text="Fail"
          />
          <custom-badge
            v-if="
              !loading && assessmentResult == 'pass' && overallOverride !== true
            "
            bg-color-class="bg-green-200"
            color-class="text-green-600"
            size="md"
            class="inline-flex self-start capitalize"
            text="Pass"
          />
          <custom-badge
            v-if="
              !loading &&
              assessmentResult == 'partial_pass' &&
              overallOverride !== true
            "
            bg-color-class="bg-green-200"
            color-class="text-green-600"
            size="md"
            class="inline-flex self-start capitalize"
            text="Partial Pass"
          />
          <custom-badge
            v-if="!loading && assessmentResult == 'incomplete'"
            bg-color-class="bg-placeholder-gray"
            color-class="text-dark-gray"
            size="md"
            class="inline-flex self-start capitalize"
            text="Incomplete"
          />
          <custom-badge
            v-if="
              !loading && assessmentResult == 'pass' && overallOverride == true
            "
            bg-color-class="bg-blue-200"
            color-class="text-blue-600"
            size="md"
            class="inline-flex self-start capitalize"
            text="Pass"
          />
          <custom-badge
            v-if="
              !loading &&
              assessmentResult == 'partial_pass' &&
              overallOverride == true
            "
            bg-color-class="bg-blue-200"
            color-class="text-blue-600"
            size="md"
            class="inline-flex self-start capitalize"
            text="Partial Pass"
          />
        </div>
      </div>
      <div>
        <div>
          <div class="mt-4 flex justify-between gap-4">
            <div
              v-if="broker?.headquarters?.address"
              class="flex items-start gap-1 text-gray-over-dark"
            >
              <svg
                class="h-4 w-auto"
                viewBox="0 0 16 17"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <g clip-path="url(#clip0_569_29008)">
                  <path
                    d="M14 7.05762C14 11.7243 8 15.7243 8 15.7243C8 15.7243 2 11.7243 2 7.05762C2 5.46632 2.63214 3.94019 3.75736 2.81498C4.88258 1.68976 6.4087 1.05762 8 1.05762C9.5913 1.05762 11.1174 1.68976 12.2426 2.81498C13.3679 3.94019 14 5.46632 14 7.05762Z"
                    stroke="#A9ADBD"
                    stroke-width="1.33333"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                  <path
                    d="M8 9.05762C9.10457 9.05762 10 8.16219 10 7.05762C10 5.95305 9.10457 5.05762 8 5.05762C6.89543 5.05762 6 5.95305 6 7.05762C6 8.16219 6.89543 9.05762 8 9.05762Z"
                    stroke="#A9ADBD"
                    stroke-width="1.33333"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </g>
                <defs>
                  <clipPath id="clip0_569_29008">
                    <rect
                      width="16"
                      height="16"
                      fill="white"
                      transform="translate(0 0.390625)"
                    />
                  </clipPath>
                </defs>
              </svg>
              <div class="flex flex-col">
                <a
                  v-if="!loading"
                  :href="handleOpenGoogleMaps(broker?.headquarters)"
                  target="_blank"
                  class="text-yellow-500 underline hover:text-yellow-700"
                >
                  <div
                    v-if="broker?.headquarters?.address"
                    class="flex"
                  >
                    <p class="text-xs uppercase">{{ formattedAddress }}</p>
                  </div>
                </a>
                <div
                  v-else
                  class="loading-light inline-block h-4 w-32"
                />
              </div>
            </div>
            <div
              v-else
              class="relative flex items-start gap-1 text-gray-over-dark"
            >
              <svg
                class="h-4 w-auto"
                viewBox="0 0 16 17"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <g clip-path="url(#clip0_569_29008)">
                  <path
                    d="M14 7.05762C14 11.7243 8 15.7243 8 15.7243C8 15.7243 2 11.7243 2 7.05762C2 5.46632 2.63214 3.94019 3.75736 2.81498C4.88258 1.68976 6.4087 1.05762 8 1.05762C9.5913 1.05762 11.1174 1.68976 12.2426 2.81498C13.3679 3.94019 14 5.46632 14 7.05762Z"
                    stroke="#A9ADBD"
                    stroke-width="1.33333"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                  <path
                    d="M8 9.05762C9.10457 9.05762 10 8.16219 10 7.05762C10 5.95305 9.10457 5.05762 8 5.05762C6.89543 5.05762 6 5.95305 6 7.05762C6 8.16219 6.89543 9.05762 8 9.05762Z"
                    stroke="#A9ADBD"
                    stroke-width="1.33333"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </g>
                <defs>
                  <clipPath id="clip0_569_29008">
                    <rect
                      width="16"
                      height="16"
                      fill="white"
                      transform="translate(0 0.390625)"
                    />
                  </clipPath>
                </defs>
              </svg>
              <p class="mb-0 text-sm">None Listed</p>
            </div>
            <div class="-mt-2">
              <h4 class="mb-1 text-2xs font-bold uppercase text-light-gray">
                Authority Age
              </h4>
              <div
                v-if="!loading"
                class="flex items-center gap-1 font-bold"
              >
                <p class="text-xs uppercase text-yellow-500">
                  {{ broker?.authority_age || '-' }}
                </p>
                <svg
                  v-if="broker?.authority_age?.length > 0"
                  width="13"
                  height="13"
                  viewBox="0 0 13 13"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    fill-rule="evenodd"
                    clip-rule="evenodd"
                    d="M6.30466 12.2119C9.35139 12.2119 11.8212 9.74201 11.8212 6.69529C11.8212 5.8144 11.6148 4.98173 11.2476 4.24303L6.49714 8.99348L5.93988 9.55074L5.38262 8.99348L2.95534 6.56621L4.06986 5.45169L5.93988 7.32171L10.3341 2.92748C9.32739 1.8513 7.89458 1.17871 6.30466 1.17871C3.25794 1.17871 0.788086 3.64857 0.788086 6.69529C0.788086 9.74201 3.25794 12.2119 6.30466 12.2119Z"
                    fill="#54C774"
                  />
                </svg>
              </div>
              <div
                v-else
                class="loading-light inline-block h-4 w-full"
              />
            </div>
          </div>
        </div>
      </div>
      <div class="mt-4 flex flex-col gap-3">
        <button-main
          v-if="!loading"
          class="button-main py-2"
          variant="light-white"
          @click="handleReportFraud"
        >
          <span class="whitespace-nowrap">Report Fraud</span>
        </button-main>
        <div v-if="showConnectButton">
          <ConnectButton
            v-if="!loading"
            :status="getConnectStatus"
            :is-for-mobile="true"
            @press="connectionButtonPress"
          />
          <div
            v-else
            class="loading-light button-main h-12"
          />
        </div>
      </div>
    </section>
    <section
      v-else
      class="container-max mx-auto flex w-full flex-col gap-4 px-4 md:px-10 lg:px-0 2xl:px-10"
      :style="{
        padding: screenWidth >= 1450 && screenWidth <= 1536 ? '0 30px' : '',
      }"
    >
      <div class="flex">
        <div class="-ml-2">
          <popper-badge
            v-if="!loading"
            :is-header-show="true"
            color-scheme="light"
          />
        </div>
        <h1
          v-if="!loading"
          class="broker-name mr-4 text-3xl text-white sm:text-2xl"
        >
          {{ getName(broker) }}
        </h1>
        <div
          v-else
          style="width: 400px !important"
          class="mr-10"
        >
          <div class="loading-light h-10" />
        </div>
        <div
          v-if="assessmentResult"
          class="mr-3 flex h-10 select-none items-center pt-1.5"
        >
          <custom-badge
            v-if="!loading && assessmentResult == 'fail'"
            bg-color-class="bg-orange-200"
            color-class="text-orange-600"
            size="lg"
            class="inline-flex self-start capitalize"
            text="Fail"
          />
          <custom-badge
            v-if="
              !loading && assessmentResult == 'pass' && overallOverride !== true
            "
            bg-color-class="bg-green-200"
            color-class="text-green-600"
            size="lg"
            class="inline-flex self-start capitalize"
            text="Pass"
          />
          <custom-badge
            v-if="
              !loading &&
              assessmentResult == 'partial_pass' &&
              overallOverride !== true
            "
            bg-color-class="bg-green-200"
            color-class="text-green-600"
            size="lg"
            class="inline-flex self-start capitalize"
            text="Partial Pass"
          />
          <custom-badge
            v-if="!loading && assessmentResult == 'incomplete'"
            bg-color-class="bg-placeholder-gray"
            color-class="text-dark-gray"
            size="lg"
            class="inline-flex self-start capitalize"
            text="Incomplete"
          />
          <custom-badge
            v-if="
              !loading && assessmentResult == 'pass' && overallOverride == true
            "
            bg-color-class="bg-blue-200"
            color-class="text-blue-600"
            size="lg"
            class="inline-flex self-start capitalize"
            text="Pass"
          />
          <custom-badge
            v-if="
              !loading &&
              assessmentResult == 'partial_pass' &&
              overallOverride == true
            "
            bg-color-class="bg-blue-200"
            color-class="text-blue-600"
            size="lg"
            class="inline-flex self-start capitalize"
            text="Partial Pass"
          />
        </div>
        <div
          id="mc-wrap"
          style="margin-top: 10px"
        >
          <div
            v-if="!loading"
            class="flex flex-1 flex-wrap items-center gap-x-2 md:ml-3"
          >
            <span
              v-for="identifier in broker?.identifiers"
              :key="identifier.value"
              class="whitespace-nowrap text-base uppercase text-gray-over-dark md:text-sm"
            >
              {{ formatIsType(identifier.is_type) }} {{ identifier.value }}
            </span>
          </div>
          <div
            v-else
            class="flex items-center justify-center gap-4"
          >
            <div class="loading-light inline-block h-4 w-10" />
            <div class="loading-light inline-block h-4 w-10" />
          </div>
        </div>
      </div>
      <div>
        <div class="flex gap-4">
          <div
            v-if="broker?.headquarters?.address"
            class="flex items-start gap-1 text-gray-over-dark"
          >
            <svg
              class="h-4 w-auto"
              viewBox="0 0 16 17"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <g clip-path="url(#clip0_569_29008)">
                <path
                  d="M14 7.05762C14 11.7243 8 15.7243 8 15.7243C8 15.7243 2 11.7243 2 7.05762C2 5.46632 2.63214 3.94019 3.75736 2.81498C4.88258 1.68976 6.4087 1.05762 8 1.05762C9.5913 1.05762 11.1174 1.68976 12.2426 2.81498C13.3679 3.94019 14 5.46632 14 7.05762Z"
                  stroke="#A9ADBD"
                  stroke-width="1.33333"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
                <path
                  d="M8 9.05762C9.10457 9.05762 10 8.16219 10 7.05762C10 5.95305 9.10457 5.05762 8 5.05762C6.89543 5.05762 6 5.95305 6 7.05762C6 8.16219 6.89543 9.05762 8 9.05762Z"
                  stroke="#A9ADBD"
                  stroke-width="1.33333"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </g>
              <defs>
                <clipPath id="clip0_569_29008">
                  <rect
                    width="16"
                    height="16"
                    fill="white"
                    transform="translate(0 0.390625)"
                  />
                </clipPath>
              </defs>
            </svg>
            <div
              v-if="!loading"
              class="flex flex-col"
            >
              <a
                :href="handleOpenGoogleMaps(broker?.headquarters)"
                target="_blank"
                class="text-yellow-500 underline hover:text-yellow-700"
              >
                <div
                  v-if="broker?.headquarters?.address"
                  class="flex underline"
                >
                  <p class="text-xs uppercase">{{ formattedAddress }}</p>
                </div>
              </a>
            </div>
            <div
              v-else
              class="loading-light inline-block h-4 w-32"
            />
          </div>
          <div
            v-else
            class="relative flex items-start gap-1 text-gray-over-dark"
          >
            <svg
              class="h-4 w-auto"
              viewBox="0 0 16 17"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <g clip-path="url(#clip0_569_29008)">
                <path
                  d="M14 7.05762C14 11.7243 8 15.7243 8 15.7243C8 15.7243 2 11.7243 2 7.05762C2 5.46632 2.63214 3.94019 3.75736 2.81498C4.88258 1.68976 6.4087 1.05762 8 1.05762C9.5913 1.05762 11.1174 1.68976 12.2426 2.81498C13.3679 3.94019 14 5.46632 14 7.05762Z"
                  stroke="#A9ADBD"
                  stroke-width="1.33333"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
                <path
                  d="M8 9.05762C9.10457 9.05762 10 8.16219 10 7.05762C10 5.95305 9.10457 5.05762 8 5.05762C6.89543 5.05762 6 5.95305 6 7.05762C6 8.16219 6.89543 9.05762 8 9.05762Z"
                  stroke="#A9ADBD"
                  stroke-width="1.33333"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </g>
              <defs>
                <clipPath id="clip0_569_29008">
                  <rect
                    width="16"
                    height="16"
                    fill="white"
                    transform="translate(0 0.390625)"
                  />
                </clipPath>
              </defs>
            </svg>
            <p class="mb-0 text-sm">None Listed</p>
          </div>
        </div>
      </div>
      <div class="flex items-center justify-between">
        <div>
          <h4 class="mb-1 text-2xs font-bold uppercase text-light-gray">
            Authority Age
          </h4>
          <div
            v-if="!loading"
            class="flex items-center gap-1 font-bold"
          >
            <p class="text-xs uppercase text-yellow-500">
              {{ broker?.authority_age || '-' }}
            </p>
            <svg
              v-if="broker?.authority_age?.length > 0"
              width="13"
              height="13"
              viewBox="0 0 13 13"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                fill-rule="evenodd"
                clip-rule="evenodd"
                d="M6.30466 12.2119C9.35139 12.2119 11.8212 9.74201 11.8212 6.69529C11.8212 5.8144 11.6148 4.98173 11.2476 4.24303L6.49714 8.99348L5.93988 9.55074L5.38262 8.99348L2.95534 6.56621L4.06986 5.45169L5.93988 7.32171L10.3341 2.92748C9.32739 1.8513 7.89458 1.17871 6.30466 1.17871C3.25794 1.17871 0.788086 3.64857 0.788086 6.69529C0.788086 9.74201 3.25794 12.2119 6.30466 12.2119Z"
                fill="#54C774"
              />
            </svg>
          </div>
          <div
            v-else
            class="loading-light inline-block h-4 w-full"
          />
        </div>
        <div class="flex gap-2 md:gap-1 lg:gap-5">
          <button-main
            v-if="!loading"
            class="button-main"
            variant="light-white"
            @click="handleReportFraud"
          >
            <span class="whitespace-nowrap">Report Fraud</span>
          </button-main>
          <div v-if="showConnectButton">
            <ConnectButton
              v-if="!loading"
              :status="getConnectStatus"
              class="w-1/2 text-base"
              :disabled="disabled"
              @press="connectionButtonPress"
            />
            <div
              v-else
              class="loading-light button-main mr-4 h-12"
            />
          </div>
        </div>
      </div>
    </section>
  </div>

  <identity-alert-modal
    v-if="showIdentityAlertModal"
    :visible="showIdentityAlertModal"
    :show-create-alert-success="showCreateAlertSuccess"
    @close="onCloseCreateAlert"
    @submit-alert="submitIdentityAlert"
  />

  <request-verification-modal
    v-if="showRequestVerificationModal"
    :visible="showRequestVerificationModal"
    :broker="broker"
    @close="closeRequestVerificationModal"
  />
</template>

<script>
import PopperBadge from '@/components/PopperBadge.vue';
import CustomBadge from '@/components/CustomBadge.vue';
import config from '@/config.js';
import ConnectButton from '@/components/show/reusable/ConnectButton.vue';
import ButtonMain from '@/components/ButtonMain.vue';
import RequestVerificationModal from '@/components/modals/RequestVerificationModal.vue';
import IdentityAlertModal from '../modals/IdentityAlertModal.vue';
import CONFIG from '@/config';
import { isSuccessfulServerResponse } from '@/common/handleServerResponse';

export default {
  name: 'HeaderShow',
  components: {
    CustomBadge,
    PopperBadge,
    ConnectButton,
    ButtonMain,
    RequestVerificationModal,
    IdentityAlertModal,
  },
  props: {
    broker: Object,
    modifiedContactInformation: Function,
    assessmentResult: String,
    overallOverride: Boolean,
    loading: Boolean,
    disabled: Boolean,
  },
  data() {
    return {
      screenWidth: window.innerWidth,
      showRequestVerificationModal: false,
      showIdentityAlertModal: false,
      showCreateAlertSuccess: false,
    };
  },
  computed: {
    formattedAddress() {
      const { headquarters } = this.broker || {};
      const { address } = headquarters || {};

      const street = [address?.street1, address?.street2]
        .filter(Boolean)
        .join(', ');
      const details =
        `${address?.city || ''}, ${address?.state || ''} ${address?.postal_code || ''}`.trim();

      return [street, details].filter(Boolean).join(' ');
    },
    isVerified() {
      const { broker_company_id } = this.broker || {};

      return broker_company_id;
    },
    canConnect() {
      const { connection } = this.broker || {};

      return !!connection;
    },
    showConnectButton() {
      if (this.isVerified && !this.canConnect) {
        return false;
      }
      return true;
    },
    getConnectStatus() {
      const { connection } = this.broker || {};

      const isConnected = connection?.status === 'onboarded';
      const inProgress = connection?.status === 'needs_to_onboard';

      if (!connection)
        return this.isVerified ? 'connect' : 'requestVerification';
      if (this.isVerified) {
        if (isConnected) return 'brokerPortal';
        if (inProgress) return 'connecting';
      }
      return 'requestVerification';
    },
    getConnectionDetails() {
      const { broker_company_id } = this.broker || {};

      if (broker_company_id) {
        const findBrokerConnection = this.$store.state.brokers.connections.find(
          (connection) => connection.company_id === broker_company_id
        );
        return findBrokerConnection;
      }
      return {};
    },
  },
  mounted() {
    window.addEventListener('resize', this.updateScreenWidth);
  },

  beforeUnmount() {
    window.removeEventListener('resize', this.updateScreenWidth);
  },
  methods: {
    getName(broker) {
      const { legal_name, name, dba_name } = broker || {};
      return legal_name || name || dba_name || '-';
    },
    sendVerificationEmail() {
      const email = '';
      const subject = encodeURIComponent(
        'Please verify your identity in Highway'
      );
      const body = encodeURIComponent(
        `Hi there,\n\nWill you please sign up with Highway to verify your identity? This will just take a minute and it will help protect your account from identity theft.\n\n${config.HWY_ONBOARD_URL}\n\nHighway provides carrier and broker identity verification and digital connections to carriers, third-party logistics companies, and freight brokers in order to create a more secure relationship between parties.\n\nThanks!`
      );

      const mailtoLink = `mailto:${email}?subject=${subject}&body=${body}`;
      window.location.href = mailtoLink;
    },
    updateScreenWidth() {
      this.screenWidth = window.innerWidth;
    },
    closeRequestVerificationModal() {
      this.showRequestVerificationModal = false;
    },
    formatIsType(type) {
      if (!type) return '';
      return type?.replace('_', ' ');
    },
    handleOpenGoogleMaps(headquarters) {
      if (!headquarters?.address) {
        return;
      }
      const { street1, city, state, postal_code } = headquarters.address;

      if (!street1 || !city || !state || !postal_code) {
        return;
      }

      const address = `${street1}, ${city}, ${state} ${postal_code}`;
      return `https://www.google.com/maps/place/${encodeURIComponent(address)}`;
    },
    handleReportFraud() {
      this.showIdentityAlertModal = true;
    },
    onCloseCreateAlert: function () {
      this.showCreateAlertSuccess = false;
      this.showIdentityAlertModal = false;
    },
    formatIdentityAlertError(response) {
      const errorMessages = {
        'identity_alert_facets.email': 'Please provide a valid email format',
        origin_area_id: 'Please insert a valid city or zip code',
      };

      const defaultMessage =
        "Sorry, we couldn't process your request, please try again.";

      if (!response || !response.data) {
        return [defaultMessage];
      }

      const { data } = response;

      if (typeof data === 'string') {
        return [data];
      }

      if (typeof data === 'object' && Object.keys(data).length > 0) {
        const errorKeys = Object.keys(data);

        const errorMessagesArray = errorKeys.map((key) => {
          return errorMessages[key] || `Error in field ${key}`;
        });

        return errorMessagesArray;
      }

      return [defaultMessage];
    },
    async submitIdentityAlert(request) {
      const payload = {
        ...request,
        carrier_id: this.$route?.params?.id,
      };
      let res = await this.$store.dispatch(
        'identityAlerts/createIdentityAlert',
        payload
      );

      if (res && isSuccessfulServerResponse(res)) {
        this.showCreateAlertSuccess = true;

        //Intercom event:
        // this.$store.dispatch('users/sendIntercomEvent', {
        //   event_name: 'submitted_identity_alert',
        //   metadata: {
        //     name: {
        //       url: window.location.href,
        //       value: this.$store.state.carriers.showCarrier?.name,
        //     },
        //     carrier_id: this.$store.state.carriers.showCarrier?.id,
        //     mc: this.$store.state.carriers?.showCarrier?.identifiers
        //       ?.filter((identifier) => identifier?.is_type == 'MC')?.[0]
        //       ?.value?.toString(),
        //     dot: this.$store.state.carriers?.showCarrier?.identifiers
        //       ?.filter((identifier) => identifier?.is_type == 'DOT')?.[0]
        //       ?.value?.toString(),
        //     report_type: request?.is_type,
        //     incident_date: request?.file_date,
        //     load_origin: request?.origin_postal_code,
        //     load_commodity_type:
        //       this.$store.state.identityAlerts.commodities.filter(
        //         (commodity) => commodity.id == request?.commodity_type_id
        //       )?.[0]?.name,
        //     suspect_email: request?.email,
        //     suspect_phone: request?.phone_e164,
        //     detailed_incident_summary: request?.message,
        //   },
        // });
      }
      if (res?.status !== 201) {
        const errorMessages = this.formatIdentityAlertError(res);

        errorMessages.forEach((errorMessage) => {
          this.$toast.open({
            message: errorMessage,
            type: 'error',
            position: 'top-right',
            duration: 5000,
          });
        });
        // this.$rollbar?.error('Error trying to create an Identity Alert', {
        //   alert: res,
        // });
      }
    },
    async connectionButtonPress() {
      const connectionStatus = this.getConnectStatus;

      switch (connectionStatus) {
        case 'requestVerification': {
          this.showRequestVerificationModal = true;
          return;
        }
        case 'connect': {
          const { connection } = this.broker || {};
          let link = connection?.sign_in_with_highway_link;

          if (CONFIG.ENV_URL?.includes('localhost')) {
            link = link.replace(
              'https://staging.highway.com',
              process.env.ENV_URL
            );
          }
          window.open(link, '_blank');
          return;
        }
        case 'brokerPortal':
        case 'connecting': {
          const { client_id, redirect_uri } = this.getConnectionDetails;
          let encodedURI = encodeURIComponent(redirect_uri);
          window.open(
            `${CONFIG.ENV_URL}${CONFIG.BASE_URL}oauth/authorizations?response_type=code&client_id=${client_id}&scope=onboarding&redirect_uri=${encodedURI}`,
            '_blank'
          );
          return;
        }
        default:
          return;
      }
    },
  },
};
</script>

<style scoped>
.button-main {
  min-width: 187px;
  @apply cursor-pointer;
}

.equipment-wrapper {
  width: 354px;
}

@media (min-width: 1152px) {
  .name-wrapper {
    display: flex;
    align-items: flex-start;
  }

  #mc-wrap {
    max-width: 20rem !important;
  }
}

.btn-size-lg {
  padding: 15px 24px;
  @apply text-lg !important;
}

.yellow {
  color: #f0c848;
}

.loading-light-2 {
  animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
  border-radius: 5px;
  background: rgba(255, 255, 255, 0.1);
  color: transparent !important;
  min-width: 40%;
}
</style>
